import type { IsoInstant, Weekday } from '@selogate/core'
import { intlTag, type Locale } from './locales'

/** The pilot is Israel (contracts.md §11 / access.ts's own `DEFAULT_SCHEDULE_TIME_ZONE`). */
export const DISPLAY_TIME_ZONE = 'Asia/Jerusalem'

/**
 * Sunday-first, matching `packages/core/src/access.ts`'s own `WEEKDAYS` ordering — the
 * schedule evaluator and the calendar UI must agree on which day is first, or a schedule
 * that reads correctly server-side renders wrong client-side.
 */
export const WEEKDAY_ORDER: readonly Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export function formatDateTime(
  instant: IsoInstant,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = {},
): string {
  return new Intl.DateTimeFormat(intlTag(locale), {
    timeZone: DISPLAY_TIME_ZONE,
    ...options,
  }).format(new Date(instant))
}

export function formatDate(instant: IsoInstant, locale: Locale): string {
  return formatDateTime(instant, locale, { year: 'numeric', month: '2-digit', day: '2-digit' })
}

/** 24-hour time, matching the prototype's clock convention throughout. */
export function formatTime(instant: IsoInstant, locale: Locale): string {
  return formatDateTime(instant, locale, { hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * "3 hours ago" / "לפני 3 שעות" — §7.11's locale-aware relative time.
 *
 * **It lives here rather than in the screen that wanted it first.** P15 §8 rule 3 names this
 * formatter by name: *"the relative-time formatter the Devices rows want … those are shell
 * components, not local helpers duplicated twice inside this PRD."* `Intl.RelativeTimeFormat`
 * carries both locales' grammar and RTL word order itself, which is the whole reason not to
 * hand-build the string per screen.
 *
 * Two deliberate choices. The unit is the **largest that fits** — a session last seen four days
 * ago reads "4 days ago", never "96 hours ago". And a future instant (clock skew between the
 * device and the server, which is real and unfixable client-side) formats as *now* rather than
 * as a negative duration promising something that has not happened.
 *
 * `numeric: 'auto'` is what turns −1 day into "yesterday" instead of "1 day ago" in both
 * catalogs, and it is the one place this app gets that phrasing for free.
 *
 * See {@link stripIcuDisambiguation} for the one thing it does not get for free.
 */
export function formatRelativeTime(instant: IsoInstant, locale: Locale, now: Date = new Date()): string {
  const format = new Intl.RelativeTimeFormat(intlTag(locale), { numeric: 'auto' })
  const seconds = Math.round((new Date(instant).getTime() - now.getTime()) / 1000)
  const past = Math.min(0, seconds)
  const units: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * 24 * 3600],
    ['month', 30 * 24 * 3600],
    ['day', 24 * 3600],
    ['hour', 3600],
    ['minute', 60],
  ]
  for (const [unit, size] of units) {
    const value = Math.trunc(past / size)
    if (value !== 0) return stripIcuDisambiguation(format.format(value, unit))
  }
  return stripIcuDisambiguation(format.format(0, 'second'))
}

/**
 * The civil date in `DISPLAY_TIME_ZONE`, as `YYYY-MM-DD`.
 *
 * `en-CA` is the shortest route to ISO-ordered parts out of `Intl`; the locale is an
 * implementation detail of the *shape*, not of anything a resident reads, so it is deliberately
 * not `intlTag(locale)` — this string is parsed, never rendered.
 */
const CIVIL_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** That civil date as a UTC epoch stamp, so two of them subtract to whole days. */
function civilDayStamp(date: Date): number {
  const [year, month, day] = CIVIL_DATE.format(date).split('-')
  return Date.UTC(Number(year), Number(month) - 1, Number(day))
}

/**
 * How many **calendar days** back an instant falls, in `DISPLAY_TIME_ZONE` — not how many
 * 24-hour chunks have elapsed.
 *
 * That distinction is the whole reason this exists. 23:50 → 00:10 the next morning is twenty
 * minutes of elapsed time and **one day**, which is what a resident means by "yesterday"; the
 * naive `(now - instant) / 86_400_000` reads it as 0 and puts a clock time on a tile for a gate
 * nobody opened today.
 *
 * **Both instants are rendered to a civil date first and the diff is taken on `Date.UTC` of the
 * parsed parts.** Subtracting epoch milliseconds and dividing by 86,400,000 is the bug to avoid:
 * Israel's spring-forward day is 23 hours long (see the DST case in `format.test.ts`), so a pair
 * spanning it is a day apart on the calendar and 0.96 days apart on the clock. `Date.UTC` has no
 * DST at all, which is what makes the subtraction exact rather than merely usually right.
 *
 * A future instant clamps to 0 — it reads as *today*, never as a negative day count — matching
 * {@link formatRelativeTime}'s own future clamp in this file and for the same reason: clock skew
 * between the device and the server is real and unfixable client-side.
 */
export function calendarDaysAgo(instant: IsoInstant, now: Date = new Date()): number {
  const days = (civilDayStamp(now) - civilDayStamp(new Date(instant))) / 86_400_000
  return Math.max(0, days)
}

/**
 * "yesterday" / "אתמול" / "לפני 5 ימים" — **which day**, with no time of day in it.
 *
 * This and {@link formatRelativeTime} answer different questions, which is why this is a second
 * function rather than an option on that one. `formatRelativeTime` answers *how long ago* in the
 * largest unit that fits, so an instant nine hours old is "9 hours ago" whichever side of midnight
 * it sits. This answers *which calendar day*, so the same instant is "yesterday" or "today"
 * depending on exactly that. A gate opened at 23:50 wants the second answer at 00:10.
 *
 * `numeric: 'auto'` is what buys אתמול / שלשום / ieri / вчора from CLDR rather than from five
 * catalogs — the same clause `formatRelativeTime` relies on, and the reason no catalog changed
 * for this.
 *
 * The {@link stripIcuDisambiguation} wrap is **not optional**: Chrome appends `(1)` / `(2)` to
 * Hebrew's singular and dual relative forms, and אתמול and שלשום are precisely those two forms —
 * so this function's two commonest outputs are the two that carry the marker. Node's ICU emits
 * neither, so **no unit test in this repo can catch a missing strip**; it fails only on the phone.
 */
export function formatRelativeDay(instant: IsoInstant, locale: Locale, now: Date = new Date()): string {
  const format = new Intl.RelativeTimeFormat(intlTag(locale), { numeric: 'auto' })
  return stripIcuDisambiguation(format.format(-calendarDaysAgo(instant, now), 'day'))
}

/**
 * Removes a trailing `(1)` / `(2)` that some ICU builds append to Hebrew relative-time strings.
 *
 * **Observed, not anticipated.** Chrome 148 renders `format(-1, 'minute')` in `he` as
 * `"לפני דקה (1)"` and `format(-2, 'month')` as `"לפני חודשיים (2)"`, on both `numeric` settings —
 * a CLDR disambiguation marker for the singular and dual forms, which is editorial metadata rather
 * than something to show a resident. Node's ICU emits neither, which is exactly why **no unit test
 * could have found this and the browser did**: the same code that renders "שלשום" correctly under
 * `vitest` renders "לפני דקה (1)" on the phone. Both magnitudes are common on this screen — the
 * two smallest a session can report.
 *
 * The pattern is deliberately narrow: a run of digits in parentheses at the very end, with nothing
 * after it. No relative phrase this app renders, in either catalog, legitimately ends that way —
 * an interpolated count would be `{{count}}` inside the catalog string, never appended here.
 */
export function stripIcuDisambiguation(formatted: string): string {
  return formatted.replace(/\s*\(\d+\)$/, '')
}

/**
 * `00:24`-style `mm:ss` countdown, widening to `H:MM:SS` at an hour and above.
 *
 * **The hours branch is D248's.** This was `mm:ss` unconditionally, which was right while the
 * only countdown was the 30 s resend timer; the cap's ceiling tier releases up to five hours
 * out, and minute-only formatting renders that as `300:00` — a number that is not wrong so much
 * as unreadable, and that reads as five *minutes* to anyone who does not stop to divide.
 *
 * Hours are **not** zero-padded: `4:52:10` is a duration, and `04:52:10` reads as a time of day.
 * Minutes and seconds keep their padding, because within a duration they are fixed-width fields.
 *
 * It stays a digit clock rather than a phrase, and that is what keeps it translatable — a
 * `One`/`Other` pair cannot express Russian's 1 / 2–4 / 5+ (`adding-a-locale.md` §3), so
 * "try again in {{n}} minutes" is wrong for 2, 3 and 4. Nothing here agrees with a noun.
 */
export function formatCountdown(seconds: number): string {
  const value = Math.max(0, Math.trunc(seconds))
  const hours = Math.floor(value / 3600)
  const minutes = Math.floor((value % 3600) / 60)
  const rest = value % 60
  const mm = String(minutes).padStart(2, '0')
  const ss = String(rest).padStart(2, '0')
  if (hours > 0) return `${String(hours)}:${mm}:${ss}`
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${ss}`
}
